/**
 * Streaming-safe markdown renderer.
 *
 * Replaces the legacy react-markdown `MarkdownMessage`. Streamdown handles
 * incomplete markdown gracefully during token streaming (`parseIncompleteMarkdown`)
 * and hardens unsafe links. We theme it purely through MUI component overrides
 * (ported from MarkdownMessage) and deliberately do NOT import Streamdown's
 * Tailwind stylesheet — keeping the app Tailwind-free.
 *
 * UUID sanitization (carry-forward) is applied here, at the render boundary.
 */

import React from 'react';
import { Streamdown, type Components } from 'streamdown';
import { Box, Link, Typography } from '@mui/material';

import { sanitizeUiText } from '../engine/sanitize';

const components: Components = {
  p: ({ children }) => (
    <Typography variant="body2" sx={{ m: 0, mb: 0.75, '&:last-child': { mb: 0 } }}>
      {children}
    </Typography>
  ),
  h1: ({ children }) => (
    <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 0.75, mb: 0.25 }}>
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 0.5, mb: 0.25 }}>
      {children}
    </Typography>
  ),
  a: ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
      {children}
    </Link>
  ),
  // `listStyleType` is explicit because Tailwind Preflight sets `list-style: none` on
  // every list. Without it these keep their indent and lose their bullets and numbers,
  // which reads as a formatting bug in every AI response containing a list. The nested
  // selectors mirror the browser: `ul` varies by depth, `ol` stays decimal throughout.
  ul: ({ children }) => (
    <Box
      component="ul"
      sx={{
        my: 0.5,
        pl: 2.5,
        listStyleType: 'disc',
        '& ul': { listStyleType: 'circle' },
        '& ul ul': { listStyleType: 'square' },
      }}
    >
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ my: 0.5, pl: 2.5, listStyleType: 'decimal' }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" variant="body2" sx={{ mb: 0.25 }}>
      {children}
    </Typography>
  ),
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        bgcolor: 'grey.900',
        color: 'grey.50',
        p: 1.5,
        borderRadius: 1,
        overflowX: 'auto',
        my: 1,
        fontSize: '0.8125rem',
        lineHeight: 1.6,
      }}
    >
      {children}
    </Box>
  ),
  code: ({ className, children, ...props }) => {
    const isBlock = Boolean(className);
    if (isBlock) {
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
    return (
      <Box
        component="code"
        sx={{
          bgcolor: 'grey.200',
          px: 0.6,
          py: 0.15,
          borderRadius: 0.5,
          fontSize: '0.8125rem',
          fontFamily: '"Fira Code", "Roboto Mono", monospace',
        }}
      >
        {children}
      </Box>
    );
  },
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{
        borderLeft: '3px solid',
        borderColor: 'grey.400',
        pl: 1.5,
        ml: 0,
        my: 0.75,
        color: 'text.secondary',
      }}
    >
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <Box sx={{ overflowX: 'auto', my: 1 }}>
      <Box
        component="table"
        sx={{
          borderCollapse: 'collapse',
          width: '100%',
          fontSize: '0.8125rem',
          '& th, & td': {
            border: '1px solid',
            borderColor: 'grey.300',
            px: 1,
            py: 0.5,
            textAlign: 'left',
          },
          '& th': { bgcolor: 'grey.100', fontWeight: 600 },
        }}
      >
        {children}
      </Box>
    </Box>
  ),
  hr: () => (
    <Box
      component="hr"
      sx={{ border: 'none', borderTop: '1px solid', borderColor: 'grey.300', my: 1 }}
    />
  ),
};

interface StreamdownMessageProps {
  text: string;
}

const StreamdownMessage: React.FC<StreamdownMessageProps> = ({ text }) => (
  <Box
    sx={{
      '& > *:first-of-type': { mt: 0 },
      '& > *:last-child': { mb: 0 },
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    }}
  >
    <Streamdown parseIncompleteMarkdown components={components}>
      {sanitizeUiText(text)}
    </Streamdown>
  </Box>
);

export default React.memo(StreamdownMessage);
